'use client'

import React, { useState, useCallback, useRef, useLayoutEffect } from 'react'
import Image from 'next/image'
import { i18n, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import Advert from 'components/Advert'
import Banner from 'components/Banner'
import BannerWingie from 'components/BannerWingie'
import Campaigns from 'components/Campaigns'
import Discover from 'components/Discover'
import DiscoverWingie from 'components/DiscoverWingie'
import DomesticFlightsWingie from 'components/DomesticFlightsWingie'
import ErrorBoundary from 'components/ErrorBoundary'
import Faq from 'components/Faq'
import FeaturesSection from 'components/FeaturesSection'
import Finance from 'components/Finance'
import HeadSeo from 'components/Head'
import InformationCard from 'components/InformationCard'
import ManageMyBookingWingie from 'components/ManageMyBookingWingie'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import PopularAirlines from 'components/PopularAirlines'
import PopularTravelPosts from 'components/PopularTravelPosts'
import PromotedFlight from 'components/PromotedFlight'
import CustomOfferCard from 'components/CustomOfferCard'
import SearchForms from 'components/SearchForms'
import * as S from 'components/SearchForms/SearchForms.styled'
import ServiceCards from 'components/ServiceCards'
import TravelRoutes from 'components/TravelRoutes'
import WhyChoose from 'components/WhyChoose'
import WhyChooseRegister from 'components/WhyChooseRegister'
import WingieSearchForms from 'components/WingieSearchForm'
import { Content, SmartBannerShell } from 'components/elements/styled'

import { useAppState } from 'context/AppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useDeviceType } from 'hooks/useDeviceType'
import getBackgroundImage from 'utils/getBackgroundImage'
import { replaceWingieWithEnuygun } from 'utils/replaceWingieWithEnuygun'
import { isWingieCarRentalTabEnabled, isWingieHotelTabEnabled } from 'utils/searchForm'
import { useRedirectionBanner } from 'hooks/useRedirectionBanner'
import { membershipMarkets } from 'utils/domainURL'
import { SmartBanner, useAuthContext } from '@enuygun/frontend-components'
import type { SmartBannerData } from '@enuygun/frontend-components'
import sendTrackerLog from 'utils/tracker'
import Wingie10YearsLogo from 'components/HomePage/Wingie10YearsLogo'
import type { ResidenceCountryOption } from 'utils/residenceCountries'

interface HomePageProps {
  res: any[]
  promotedFlightsData?: any
  visaFreeCountries?: { [countryCode: string]: { isVisaFree: boolean; visaType?: string } }
  residenceCountries?: ResidenceCountryOption[]
  customOfferFlightsData?: any[]
  smartBanners?: SmartBannerData[]
  smartBannerTrackerCategory?: string
  smartBannerTrackerPage?: string
}

// NOTE: @enuygun/frontend-components tipleri her zaman publish ile aynı hizada olmayabiliyor.
// Runtime'da trackerMeta + payload'lı callback'ler geliyor; burada TS'yi pragmatik şekilde gevşetiyoruz.
const TrackedSmartBanner = SmartBanner as any

type HomeSmartBannerPayload = {
  ctxt_referrer?: string
  ctxt_href?: string
  [key: string]: unknown
}

// NOT: Localde (örn. `http://localhost:6008`) smart banner loglarını görmek istiyorsanız,
// `handleBannerImpression` / `handleBannerClick` içindeki `if (shouldSkipSmartBannerLog(payload)) return`
// satırlarını geçici olarak yorum satırına alın — ya da aşağıdaki fonksiyonun gövdesini geçici olarak `return false` yapın.
const shouldSkipSmartBannerLog = (payload: HomeSmartBannerPayload) => {
  const storybookOrigin = 'http://localhost:6008'
  if (payload.ctxt_referrer === `${storybookOrigin}/`) return true
  if (typeof payload.ctxt_href === 'string' && payload.ctxt_href.startsWith(storybookOrigin)) return true
  if (typeof globalThis !== 'undefined' && globalThis.location?.href?.startsWith(storybookOrigin)) return true
  return false
}

const HomePage: React.FC<HomePageProps> = ({
  res,
  promotedFlightsData,
  visaFreeCountries,
  residenceCountries,
  customOfferFlightsData,
  smartBanners,
  smartBannerTrackerCategory,
  smartBannerTrackerPage,
}) => {
  const { query } = useRouter()

  const [ready, setReady] = useState(false)

  React.useEffect(() => {
    if (query.tab) return

    sessionStorage.removeItem('lastCalendarRefId')

    const previousCategory = globalThis.localStorage.getItem('previous-search-category')
    // Eğer daha önce arama yapılmamışsa (localStorage boşsa) isReady true olsun
    if (previousCategory === null) {
      const timeout = setTimeout(() => {
        setReady(true)
      }, 200)
      return () => clearTimeout(timeout)
    }
    // Daha önce arama yapılmışsa isReady false kalsın
    setReady(false)
  }, [])

  const { posts } = res[1]
  const discoverWingieData = res[2]
  const travelArticles = posts

  const { market, fullLocale, domain, language } = useAppState()

  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const isWingie = market !== 'tr'
  const isTR = market == 'tr'
  const lockWingieHeroHeight = isWingie && (isWingieHotelTabEnabled(language, market) || isWingieCarRentalTabEnabled(language, market)) && !isMobile && !isTablet
  const heroRef = useRef<HTMLDivElement>(null)
  const [heroMinHeight, setHeroMinHeight] = useState(0)

  useLayoutEffect(() => {
    if (!lockWingieHeroHeight) return
    const el = heroRef.current
    if (!el) return

    const lockHeroHeight = () => {
      const height = Math.ceil(el.getBoundingClientRect().height)
      setHeroMinHeight((prev) => Math.max(prev, height))
    }

    lockHeroHeight()
    const observer = new ResizeObserver(lockHeroHeight)
    observer.observe(el)
    return () => observer.disconnect()
  }, [lockWingieHeroHeight])
  /** 10. yıl logo — TR'de yok */
  const isAnniversary = isWingie

  const resolvedSmartBannerTrackerCategory = smartBannerTrackerCategory || (isWingie ? 'wingie-homepage' : 'enuygun-homepage')
  const resolvedSmartBannerTrackerPage = smartBannerTrackerPage || (isWingie ? 'wingie-homepage' : 'enuygun-homepage')

  const authContext = useAuthContext()
  const isLoggedIn = authContext?.user && Object.keys(authContext.user).length > 0
  const isMembershipActive = membershipMarkets.includes(market as string)
  const renderWhyChooseRegister = !isLoggedIn && isMembershipActive && isWingie

  const { t } = useTranslation('common')
  const preloadBGImg = getBackgroundImage(market)
  const languageCode = i18n?.language?.includes('-') ? i18n.language?.split('-')[1] : i18n?.language
  const isRTL = i18n?.dir(languageCode) === 'rtl'

  // Dynamic values from router query and context
  const isPredirection = query.predirection === 'true'
  const fromDomain = query.from as string
  const currentHostname = typeof globalThis !== 'undefined' && globalThis.location?.hostname

  const { showBanner } = useRedirectionBanner({
    predirection: isPredirection,
    sourceDomain: fromDomain,
    targetDomain: currentHostname || '',
    language: fullLocale?.split('-')[0] || 'tr',
  })

  // Banner impression tracking - her banner için bir kere log gönder
  const bannerLoggedRef = useRef<Set<number>>(new Set())

  const handleBannerImpression = useCallback((banner: SmartBannerData, payload: HomeSmartBannerPayload) => {
    if (shouldSkipSmartBannerLog(payload)) return

    // Aynı banner için tekrar log gönderme
    if (bannerLoggedRef.current.has(banner.id)) return
    bannerLoggedRef.current.add(banner.id)

    sendTrackerLog(payload as Parameters<typeof sendTrackerLog>[0])
  }, [])

  const handleBannerClick = useCallback((_banner: SmartBannerData, _image: unknown, payload: HomeSmartBannerPayload) => {
    if (shouldSkipSmartBannerLog(payload)) return
    sendTrackerLog(payload as Parameters<typeof sendTrackerLog>[0])
  }, [])

  return (
    <>
      <HeadSeo title={t('app.meta.title')} description={t('app.meta.description')}>
        {!isMobile && !isTablet && <link rel="preload" href={preloadBGImg} as="image" />}
      </HeadSeo>
      <S.SearchFormContainer
        ref={heroRef}
        $minHeight={lockWingieHeroHeight ? heroMinHeight : undefined}
        isReady={ready}
        domain={domain}
        showBanner={showBanner}
        isMobile={isMobile}
        isTablet={isTablet}
      >
        {!isMobile && !isTablet && (
          <Image
            style={{
              pointerEvents: 'none',
              width: '100%',
              height: '100%',
              zIndex: -1,
            }}
            sizes="(min-width: 768px) 16vw, 100vw"
            src={preloadBGImg ?? 'https://cdn2.enuygun.com/media/lib/uploads/image/enuygun-homepage-ilk-bahar-gorseli-57536.webp'}
            priority
            fill
            unoptimized
            alt="Enuygun search form arka plan"
            data-testid="search-form-background-image"
          />
        )}
        <S.Content isMobile={isMobile} isTablet={isTablet} showBanner={showBanner}>
          {!isTR && isMobile && <S.HeadTitle>{t('searchForm.headTitle')}</S.HeadTitle>}
          {!isMobile && !isTablet && (
            <>
              <S.IntroTitleWrapper>
                <S.IntroTitleInner dir={isRTL ? 'rtl' : 'ltr'}>
                  <S.IntroTitle data-testid="search-form-heading">{t('searchForm.heading')}</S.IntroTitle>
                  {isAnniversary && <Wingie10YearsLogo isRTL={isRTL} />}
                </S.IntroTitleInner>
              </S.IntroTitleWrapper>
              {!isTR && (
                <S.IntroSubTitle data-testid="search-form-sub-heading">{replaceWingieWithEnuygun(t('searchForm.subHeading'), market || '', language || '')}</S.IntroSubTitle>
              )}
            </>
          )}
          <ErrorBoundary fallback="">
            <React.Suspense fallback="loading">
              {isTR ? (
                <SearchForms isTablet={isTablet} autocompletePage="homepage" />
              ) : (
                <WingieSearchForms isTablet={isTablet} residenceCountries={residenceCountries} autocompletePage="homepage" />
              )}
            </React.Suspense>
          </ErrorBoundary>
          {!isMobile && !isTablet && isTR && <S.TravelDocument data-testid="search-form-travel-document">Enuygun Seyahat, Belge No: 7153</S.TravelDocument>}
        </S.Content>
      </S.SearchFormContainer>
      {smartBanners && smartBanners.length > 0 && (
        <ErrorBoundary fallback="SmartBanner">
          <Content isTablet={isTablet}>
            <SmartBannerShell $isRtl={isRTL}>
              <TrackedSmartBanner
                banners={smartBanners}
                inventory="PRODUCT_HOMEPAGE"
                isMobile={isMobile || false}
                onBannerImpression={handleBannerImpression}
                onBannerClick={handleBannerClick}
                trackerMeta={{
                  category: resolvedSmartBannerTrackerCategory,
                  page: resolvedSmartBannerTrackerPage,
                  market,
                  requestIdGenerator: getAnalyticsRequestId,
                }}
              />
            </SmartBannerShell>
          </Content>
        </ErrorBoundary>
      )}

      {isWingie && (
        <ErrorBoundary fallback="FeaturesSection">
          <FeaturesSection />
        </ErrorBoundary>
      )}

      <ErrorBoundary fallback="Promoted-Flight-Card">
        <Content isTablet={isTablet}>
          <PromotedFlight promotedFlightsData={promotedFlightsData} visaFreeCountries={visaFreeCountries} />
        </Content>
      </ErrorBoundary>

      {isTR && (
        <ErrorBoundary fallback="Custom-Offer-Card">
          <CustomOfferCard initialFlightsData={customOfferFlightsData} />
        </ErrorBoundary>
      )}

      <ErrorBoundary fallback="Advert - InformationCard">
        {isTR && (
          <Content isTablet={isTablet}>
            <Advert />
            <InformationCard />
          </Content>
        )}
      </ErrorBoundary>

      <ErrorBoundary fallback="ManageMyBookingWingie">
        {isWingie && (
          <Content isTablet={isTablet}>
            <ManageMyBookingWingie />
          </Content>
        )}
      </ErrorBoundary>

      <ErrorBoundary fallback="FinanceMobile - ServiceCards">
        {(isMobile || isTablet) && isTR && (
          <Content isTablet={isTablet}>
            <ServiceCards />
          </Content>
        )}
      </ErrorBoundary>

      {isTR && (
        <ErrorBoundary fallback="Campaigns">
          <Content isTablet={isTablet}>
            <Campaigns res={res} />
          </Content>
        </ErrorBoundary>
      )}

      <ErrorBoundary fallback="Finance">{isTR && <Finance />}</ErrorBoundary>

      {isWingie && (
        <ErrorBoundary fallback="DiscoverWingie">
          <Content isTablet={isTablet}>
            <DiscoverWingie discoverWingieData={discoverWingieData} res={res} />
          </Content>
        </ErrorBoundary>
      )}

      {isTR && (
        <ErrorBoundary fallback="Discover">
          <Content isTablet={isTablet}>
            <Discover />
          </Content>
        </ErrorBoundary>
      )}

      <ErrorBoundary fallback="WhyChoose">
        <WhyChoose res={res} isTablet={isTablet} />
      </ErrorBoundary>

      {isTR && <Banner isTablet={isTablet} />}

      {isWingie && (
        <ErrorBoundary fallback="BannerWingie">
          <BannerWingie isTablet={isTablet} />
        </ErrorBoundary>
      )}

      {renderWhyChooseRegister && (
        <ErrorBoundary fallback="WhyChooseRegister">
          <WhyChooseRegister />
        </ErrorBoundary>
      )}

      {isWingie && (
        <Content>
          <ErrorBoundary fallback="TravelRoutes">
            <TravelRoutes res={res} isTablet={isTablet} />
          </ErrorBoundary>
          <ErrorBoundary fallback="PopularAirlines">
            <PopularAirlines res={res} />
          </ErrorBoundary>
          <ErrorBoundary fallback="DomesticFlightsWingie">
            <DomesticFlightsWingie res={res} isTablet={isTablet} />
          </ErrorBoundary>
          {travelArticles?.length > 0 && (
            <ErrorBoundary fallback="PopularTravelPosts">
              <PopularTravelPosts travelArticles={travelArticles} isWingie={isWingie} fullLocale={fullLocale} />
            </ErrorBoundary>
          )}
          <Faq res={res} />
        </Content>
      )}

      {isTR && (
        <Content isTablet={isTablet}>
          <ErrorBoundary fallback="PopularTravelPosts">
            {!isMobile && !isTablet && <TravelRoutes res={res} isTablet={isTablet} />}
            <PopularTravelPosts travelArticles={travelArticles} />
          </ErrorBoundary>
        </Content>
      )}

      {(isMobile || isTablet) && isTR && (
        <Content isTablet={isTablet}>
          <ErrorBoundary fallback="TravelRoutes">
            <TravelRoutes res={res} isTablet={isTablet} />
          </ErrorBoundary>
        </Content>
      )}
    </>
  )
}

export default HomePage
